import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, TouchableOpacity, FlatList, Dimensions} from 'react-native';
import {Text, Portal, Modal, RadioButton, Divider} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const OrgUnitSelector = () => {
  const {orgUnits, selectedOrgUnitId, setSelectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [orgUnits.length, fetchOrgUnits]);

  // Don't render if only one store
  if (orgUnits.length <= 1) return null;

  const selectedName = selectedOrgUnitId
    ? orgUnits.find(o => o.id === selectedOrgUnitId)?.name || 'Store'
    : 'All Stores';

  const handleSelect = (id) => {
    setSelectedOrgUnitId(id);
    setVisible(false);
  };

  return (
    <>
      <TouchableOpacity
        style={styles.chip}
        onPress={() => setVisible(true)}
        activeOpacity={0.7}>
        <MaterialCommunityIcons name="store-outline" size={16} color={colors.white} />
        <Text style={styles.chipText} numberOfLines={1}>{selectedName}</Text>
        <MaterialCommunityIcons name="chevron-down" size={16} color={colors.white} />
      </TouchableOpacity>

      <Portal>
        <Modal
          visible={visible}
          onDismiss={() => setVisible(false)}
          contentContainerStyle={styles.modal}>
          <Text style={styles.modalTitle}>Select Store</Text>
          <Divider />

          <TouchableOpacity
            style={styles.option}
            onPress={() => handleSelect(null)}>
            <RadioButton.Android
              value="all"
              status={selectedOrgUnitId === null ? 'checked' : 'unchecked'}
              onPress={() => handleSelect(null)}
              color={colors.primary}
            />
            <Text style={styles.optionText}>All Stores</Text>
          </TouchableOpacity>

          <FlatList
            data={orgUnits}
            keyExtractor={item => String(item.id)}
            style={{flex: 1}}
            renderItem={({item}) => (
              <TouchableOpacity
                style={styles.option}
                onPress={() => handleSelect(item.id)}>
                <RadioButton.Android
                  value={String(item.id)}
                  status={selectedOrgUnitId === item.id ? 'checked' : 'unchecked'}
                  onPress={() => handleSelect(item.id)}
                  color={colors.primary}
                />
                <View style={styles.optionContent}>
                  <Text style={styles.optionText}>{item.name}</Text>
                  {item.city && <Text style={styles.optionSubtext}>{item.city}</Text>}
                </View>
              </TouchableOpacity>
            )}
          />
        </Modal>
      </Portal>
    </>
  );
};

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: spacing.sm,
    maxWidth: 160,
    gap: 4,
  },
  chipText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '500',
    flexShrink: 1,
  },
  modal: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    borderRadius: 12,
    height: Dimensions.get('window').height * 0.5,
    overflow: 'hidden',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    padding: spacing.md,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingRight: spacing.md,
  },
  optionContent: {
    flex: 1,
  },
  optionText: {
    fontSize: 15,
    color: colors.text,
  },
  optionSubtext: {
    fontSize: 12,
    color: colors.textSecondary,
  },
});

export default OrgUnitSelector;
